import { CAPABILITY_MANAGEMENT, USER_MANAGEMENT_V2 } from "api/ApiPath";
import { ActionInjection } from "app/actions/di/ActionInjection";
import { ActionRequestEntity } from "app/actions/domain/entites/ActionRequestEntity";
import { ActionUseCase } from "app/actions/domain/useCases/ActionUseCase";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { IFilterRepository } from "app/filters/domain/repository/IFilterRepository";
import { CapabilityActionRequestEntity } from "features/capabilityManagement/domain/entities/CapabilityActionRequestEntity";
import { CapabilityDetailEntity } from "features/capabilityManagement/domain/entities/CapabilityDetailEntity";
import { UserPermissionInjection } from "features/userPermissionManagement/di/UserPermissionInjection";
import { isObject, isUndefined } from "lodash";
import { injectable } from "tsyringe";
import { DataFormat } from "utils/format/DataFormat";
import { avoidDuplicate } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { UserPermissionActionRequestEntity } from "../entities/actions/UserPermissionActionRequestEntity";
import { UserPermissionCapabilityActionRequestEntity } from "../entities/actions/UserPermissionCapabilityActionRequestEntity";
import { DetailUserPermissionEntity } from "../entities/detail/DetailUserPermissionEntity";
import { UserPermissionRequestEntity } from "../entities/UserPermissionRequestEntity";
import { UserPermissionResponseEntity } from "../entities/UserPermissionResponseEntity";
import { IUserPermissionRepository } from "../repositories/IUserPermissionRepository";

@injectable()
export class UserPermissionUseCase {
  private userPermissionRepo: IUserPermissionRepository;
  private filterRepo: IFilterRepository;
  private actionUseCase: ActionUseCase;

  constructor() {
    this.actionUseCase = ActionInjection.getUseCase();
    this.userPermissionRepo = UserPermissionInjection.getRepo();
    this.filterRepo = FilterInjection.getRepo();
  }

  private checkParams(params: UserPermissionRequestEntity) {
    const finalParams = { ...params };
    delete finalParams.created;

    Object.keys(finalParams).forEach((rawKey) => {
      const key = rawKey as keyof UserPermissionRequestEntity;
      if (
        finalParams[key] === strings.GlobalLabels.All ||
        finalParams[key] === "" ||
        typeof finalParams[key] === "undefined"
      ) {
        delete finalParams[key];
      }
    });

    return finalParams;
  }

  private updateBody(
    body: UserPermissionActionRequestEntity,
    detailInfo: DetailUserPermissionEntity,
    userList: Array<BaseUserResponseModel>
  ) {
    const bodyToEntity = new UserPermissionActionRequestEntity(body);
    const keysToAvoid = ["code", "configuration", "blackList"];
    const renderedBody = Object.keys(body)?.filter(
      (item) =>
        !Array.isArray(body[item]) &&
        !keysToAvoid?.includes(item) &&
        !isUndefined(
          avoidDuplicate(
            isObject(detailInfo[item])
              ? detailInfo[item]?.["code"]
              : detailInfo[item]?.toString(),
            body[item]?.toString()
          )
        )
    );
    
    const finalBlackList = !bodyToEntity?.isUpdateAction()
      ? body?.blackList
      : this.actionUseCase.buildArrayEditActions(
          body?.blackList as Array<string>,
          userList?.filter((item) =>
            detailInfo?.blackList?.some((ele) => ele === item?.username)
          )
        );
    const afterBody = {
      code: body["code"],
      ...renderedBody?.reduce(
        (a, v) => ({ ...a, [v]: body[v]?.toString() }),
        {}
      ),
      configuration:
        typeof body?.configuration === "string" && body?.configuration
          ? JSON.parse(DataFormat.autoCorrectJSON(String(body.configuration)))
          : body?.configuration,
      blackList: finalBlackList,
    } as typeof body;

    return afterBody;
  }

  private mapCreateCapabilityBody(
    permissionCode: string,
    body: UserPermissionCapabilityActionRequestEntity
  ) {
    return new CapabilityActionRequestEntity({
      name: body?.name,
      code: body?.code,
      key: body?.key,
      displayName: body?.displayName,
      menus: body?.menus || [],
      feature: permissionCode,
      status: 0,
    });
  }

  private mapUpdateCapabilityBody(
    permissionCode: string,
    body: UserPermissionCapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity = {}
  ) {
    return new CapabilityActionRequestEntity({
      name: body?.name,
      code: body?.code,
      key: body?.key,
      displayName: body?.displayName,
      menus: this.actionUseCase.buildArrayEditActions(
        body?.menus || [],
        detailInfo?.menus || []
      ),
      feature: permissionCode,
      status: 0,
    });
  }

  async getList(
    params: UserPermissionRequestEntity
  ): Promise<ListBaseApiResponseModel<UserPermissionResponseEntity>> {
    return await this.userPermissionRepo.getList(this.checkParams(params));
  }

  async getPermissionDetail(
    params: UserPermissionRequestEntity
  ): Promise<BaseApiResponseModel<DetailUserPermissionEntity>> {
    return await this.userPermissionRepo.getDetail(this.checkParams(params));
  }

  async createPermission(
    body: UserPermissionActionRequestEntity,
    detailInfo: DetailUserPermissionEntity
  ): Promise<BaseApiResponseModel<DetailUserPermissionEntity>> {
    const updatedBody = this.updateBody(body, detailInfo, []);
    return await this.userPermissionRepo.create(updatedBody);
  }

  async updatePermission(
    body: UserPermissionActionRequestEntity,
    detailInfo: DetailUserPermissionEntity,
    userList: Array<BaseUserResponseModel>
  ): Promise<BaseApiResponseModel<DetailUserPermissionEntity>> {
    const updatedBody = this.updateBody(body, detailInfo, userList);
    return await this.userPermissionRepo.update(updatedBody);
  }

  async deletePermission(
    params: UserPermissionRequestEntity
  ): Promise<BaseApiResponseModel<DetailUserPermissionEntity>> {
    return await this.userPermissionRepo.delete(this.checkParams(params));
  }

  async createCapability(
    permissionCode: string,
    body: UserPermissionCapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity = {}
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    return await this.actionUseCase.create(
      new ActionRequestEntity({
        path: CAPABILITY_MANAGEMENT.CREATE,
        inputValue: this.mapCreateCapabilityBody(permissionCode, body),
      })
    );
  }

  async updateCapability(
    permissionCode: string,
    body: UserPermissionCapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity = {}
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    return await this.actionUseCase.update(
      new ActionRequestEntity({
        path: CAPABILITY_MANAGEMENT.UPDATE,
        inputValue: this.mapUpdateCapabilityBody(
          permissionCode,
          body,
          detailInfo
        ),
        comparedValue: detailInfo,
        keysToAvoid: ["menus"],
      })
    );
  }

  async getCapabilityDetail(params: {
    code?: string;
  }): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    return await this.actionUseCase.getDetail(
      new ActionRequestEntity({
        path: CAPABILITY_MANAGEMENT.DETAIL,
        inputValue: params,
      })
    );
  }

  async deleteCapability(params: {
    code?: string;
  }): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    return await this.actionUseCase.delete({
      path: CAPABILITY_MANAGEMENT.DELETE,
      inputValue: { code: params?.code },
    });
  }

  async getUsers(
    params: BaseUserRequestModel
  ): Promise<ListBaseApiResponseModel<BaseUserResponseModel>> {
    const path = USER_MANAGEMENT_V2.FILTER_LIST;
    return await this.filterRepo.getFilterList(path, params);
  }
}
